import ChapterBadge from "./ChapterBadge";
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { 
  CaretDown,
  Crown,
  ChartLineUp,
  Gear,
  CurrencyDollar,
  Cpu
} from "@phosphor-icons/react";

const DecisionQuestions = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [expandedRole, setExpandedRole] = useState("CEO");

  const roles = [
    {
      id: "CEO",
      icon: Crown,
      label: "CEO",
      descriptor: "Enterprise-wide direction, intervention, and priority setting",
      sublabel: "Growth and intervention",
      questions: [
        "Where is the business drifting off-plan before it becomes obvious in monthly reporting?",
        "Which 2–3 issues today have the biggest combined impact on growth, cash, and execution?",
        "What needs my direct intervention versus what should stay with the function head?",
        "Are our current growth signals high-quality, or are we masking weakness with optimistic reporting?",
        "What decision do I need to make this week that meaningfully changes the next 30–90 days?",
      ],
    },
    {
      id: "CFO",
      icon: CurrencyDollar,
      label: "CFO",
      descriptor: "Cash, margin, forecast credibility, and capital discipline",
      sublabel: "Cash and discipline",
      questions: [
        "Is revenue converting into cash at the expected pace, and where is the slippage?",
        "Which customers, products, regions, or business units are diluting margin?",
        "What assumptions in our current forecast are weakest or least credible?",
        "Where do we need immediate working-capital intervention?",
        "Which investments are improving long-term position, and which are simply depressing free cash flow without enough return visibility?",
      ],
    },
    {
      id: "COO",
      icon: Gear,
      label: "COO",
      descriptor: "Execution reliability, operational bottlenecks, and delivery risk",
      sublabel: "Execution and delivery",
      questions: [
        "Where is execution failure most likely to become a customer or revenue problem?",
        "Which bottlenecks are local, and which indicate a broader systemic issue?",
        "What part of the operating plan is creating the biggest risk to delivery, quality, or timeline?",
        "Where should resources be reallocated right now to protect output and commitments?",
        "Which operational issues deserve escalation to the CEO because they are now commercially material?",
      ],
    },
    {
      id: "CRO",
      icon: ChartLineUp,
      label: "CRO / Sales",
      descriptor: "Pipeline quality, revenue confidence, and account-level intervention",
      sublabel: "Revenue and pipeline confidence",
      questions: [
        "Which deals or accounts are most likely to slip despite appearing healthy in the pipeline?",
        "Is our pipeline quality improving, or are we carrying inflated late-stage confidence?",
        "What is the real driver of revenue risk right now: demand, conversion, pricing, sales capacity, or execution?",
        "Which accounts need executive sponsorship to protect or accelerate closure?",
        "What part of the funnel should we fix first to change near-term revenue outcomes?",
      ],
    },
    {
      id: "CIO",
      icon: Cpu,
      label: "CIO / CTO",
      descriptor: "Technology leverage, system readiness, and business-facing AI activation",
      sublabel: "Technology and activation",
      questions: [
        "Which technology investments are producing measurable business leverage today?",
        "Where is technology slowing the business because data, systems, or reporting are fragmented?",
        "Which systems are most critical to reliable executive intelligence and AI activation?",
        "What can be implemented quickly with high trust, versus what requires deeper integration or data cleanup?",
        "Where are we overinvesting in technology without enough decision-making or operating payoff?",
      ],
    },
  ];

  const toggleRole = (roleId) => {
    setExpandedRole(expandedRole === roleId ? null : roleId);
  };

  return (
    <section 
      ref={ref}
      className="py-24 md:py-32 px-6 md:px-12 lg:px-24 relative"
      data-testid="decision-questions-section"
    >
      {/* Background */}
      <div className="absolute inset-0 bg-[#0A0A0B]" />

      <div className="relative z-10 max-w-4xl mx-auto">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <ChapterBadge number="05" name="Decision Matrix" />
          <span className="mono-label">AI-Powered Decision Support</span>
          <h2 className="mt-4 text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-white font-['Outfit']">
            The questions that <span className="gradient-text">move the business</span>
          </h2>
          <p className="mt-4 text-[#A1A1AA] max-w-2xl mx-auto text-base md:text-lg leading-relaxed">
            OnTenacity uses AI to help leadership answer the questions that most directly affect 
            growth, cash, execution, and risk — with stronger context and source-backed clarity.
          </p>
        </motion.div>

        {/* Intro statement */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="text-center text-sm text-[#71717A] mb-10 max-w-xl mx-auto"
        >
          From growth and cash to execution and technology leverage, OnTenacity helps leadership 
          identify what matters, what is drifting, and what requires action next.
        </motion.p>

        {/* Role Accordion */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="space-y-3"
          data-testid="role-accordion"
        >
          {roles.map((role, index) => (
            <div
              key={role.id}
              className={`rounded-xl border transition-all duration-300 ${
                expandedRole === role.id 
                  ? "border-[#00E5FF]/30 bg-[#0F0F10]" 
                  : "border-white/5 bg-[#0F0F10]/50 hover:border-white/10"
              }`}
              data-testid={`role-card-${role.id.toLowerCase()}`}
            >
              {/* Role Header */}
              <button
                onClick={() => toggleRole(role.id)}
                className="w-full px-5 md:px-6 py-4 md:py-5 flex items-center justify-between gap-4"
                data-testid={`role-toggle-${role.id.toLowerCase()}`}
              >
                <div className="flex items-center gap-3 md:gap-4">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors ${
                    expandedRole === role.id 
                      ? "bg-[#00E5FF]/20" 
                      : "bg-white/5"
                  }`}>
                    <role.icon 
                      size={20} 
                      weight="duotone" 
                      className={expandedRole === role.id ? "text-[#00E5FF]" : "text-[#A1A1AA]"} 
                    />
                  </div>
                  <div className="text-left">
                    <h3 className={`text-base md:text-lg font-semibold font-['Outfit'] transition-colors ${
                      expandedRole === role.id ? "text-white" : "text-[#FAFAFA]"
                    }`}>
                      {role.label}
                    </h3>
                    <p className="text-xs md:text-sm text-[#71717A] hidden sm:block">
                      {role.descriptor}
                    </p>
                  </div>
                </div>
                <motion.div
                  animate={{ rotate: expandedRole === role.id ? 180 : 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <CaretDown 
                    size={20} 
                    className={expandedRole === role.id ? "text-[#00E5FF]" : "text-[#71717A]"} 
                  />
                </motion.div>
              </button>

              {/* Expanded Content */}
              <AnimatePresence>
                {expandedRole === role.id && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                    className="overflow-hidden"
                  >
                    <div className="px-5 md:px-6 pb-5 md:pb-6">
                      {/* Sublabel */}
                      <div className="mb-4 pb-4 border-b border-white/5">
                        <span className="text-xs text-[#00E5FF] uppercase tracking-wider font-medium">
                          {role.sublabel}
                        </span>
                        <p className="text-sm text-[#71717A] mt-1 sm:hidden">
                          {role.descriptor}
                        </p>
                      </div>

                      {/* Questions */}
                      <ol className="space-y-4">
                        {role.questions.map((question, qIndex) => (
                          <motion.li
                            key={qIndex}
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: qIndex * 0.05 }}
                            className="flex gap-3"
                          >
                            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-white/5 flex items-center justify-center text-xs text-[#00E5FF] font-mono">
                              {qIndex + 1}
                            </span>
                            <p className="text-sm md:text-base text-[#FAFAFA] leading-relaxed">
                              {question}
                            </p>
                          </motion.li>
                        ))}
                      </ol>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </motion.div>

        {/* Bottom Summary Strip */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-12 text-center"
        >
          <div className="inline-block px-6 py-4 rounded-xl bg-gradient-to-r from-[#0F0F10] to-[#0A0A0B] border border-white/5">
            <p className="text-sm md:text-base text-[#FAFAFA] font-medium">
              OnTenacity helps leadership move from fragmented signals to faster decisions, 
              stronger escalation, and clearer action.
            </p>
            <p className="text-xs text-[#71717A] mt-2">
              Built to surface what changed, why it matters, and what should happen next.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default DecisionQuestions;
